import { UploadedFile, SupportedFileType, ProcessingResult } from '../types/multimodal';

// Validar arquivo
export const validateFile = (
  file: File, 
  fileType: SupportedFileType, 
  maxSize: number
): boolean => {
  // Verificar tamanho
  if (file.size > maxSize) {
    return false;
  }

  // Verificar tipo MIME básico
  const validMimeTypes: Record<SupportedFileType, string[]> = {
    image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/svg+xml'],
    document: [
      'application/pdf', 
      'text/plain', 
      'text/markdown',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'text/csv',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ],
    audio: ['audio/mpeg', 'audio/wav', 'audio/mp4', 'audio/ogg', 'audio/flac'],
    video: ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/x-matroska', 'video/webm']
  };

  return validMimeTypes[fileType].includes(file.type);
};

// Processar arquivo individual
export const processFile = async (
  file: UploadedFile, 
  onProgress?: (progress: number) => void
): Promise<UploadedFile> => {
  onProgress?.(10);

  try {
    let processedFile = { ...file };

    switch (file.type) {
      case 'image':
        processedFile = await processImage(file, onProgress);
        break;
      case 'document':
        processedFile = await processDocument(file, onProgress);
        break;
      case 'audio':
        processedFile = await processAudio(file, onProgress);
        break;
      case 'video':
        processedFile = await processVideo(file, onProgress);
        break;
    }

    onProgress?.(100);
    processedFile.processed = true;
    
    return processedFile;
  } catch (error) {
    console.error('Error processing file:', error);
    throw error;
  }
};

// Processar imagem
const processImage = async (
  file: UploadedFile, 
  onProgress?: (progress: number) => void
): Promise<UploadedFile> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onprogress = (e) => {
      if (e.lengthComputable) {
        const progress = Math.round((e.loaded / e.total) * 50) + 10; // 10-60%
        onProgress?.(progress);
      }
    };

    reader.onload = (e) => {
      const result = e.target?.result as string;
      
      img.onload = () => {
        onProgress?.(80);

        // Otimizar imagem se necessário
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Cannot create canvas context'));
          return;
        }

        // Definir tamanho máximo (para economizar tokens)
        const MAX_WIDTH = 1024;
        const MAX_HEIGHT = 1024;
        
        let { width, height } = img;
        
        // Redimensionar se necessário
        if (width > MAX_WIDTH || height > MAX_HEIGHT) {
          const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        canvas.width = width;
        canvas.height = height;
        
        // Desenhar imagem otimizada
        ctx.drawImage(img, 0, 0, width, height);
        
        onProgress?.(90);

        // Converter para base64 com qualidade otimizada
        const optimizedBase64 = canvas.toDataURL('image/jpeg', 0.8);
        
        resolve({
          ...file,
          base64: optimizedBase64,
          url: result,
          metadata: {
            width: img.naturalWidth,
            height: img.naturalHeight,
            ...file.metadata
          }
        });
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      img.src = result;
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file.file);
  });
};

// Processar documento
const processDocument = async (
  file: UploadedFile, 
  onProgress?: (progress: number) => void
): Promise<UploadedFile> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onprogress = (e) => {
      if (e.lengthComputable) {
        const progress = Math.round((e.loaded / e.total) * 80) + 10; // 10-90%
        onProgress?.(progress);
      }
    };

    reader.onload = async (e) => {
      try {
        const result = e.target?.result;
        
        if (!result) {
          reject(new Error('Failed to read file'));
          return;
        }

        let extractedText = '';
        let base64Data = '';
        
        if (file.file.type === 'text/plain' || file.file.type === 'text/markdown') {
          // Arquivo de texto simples
          extractedText = result as string;
          base64Data = btoa(extractedText);
        } else if (file.file.type === 'application/pdf') {
          // PDF - converter para base64 para enviar à API
          const arrayBuffer = result as ArrayBuffer;
          const bytes = new Uint8Array(arrayBuffer);
          let binary = '';
          for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
          }
          base64Data = btoa(binary);
          
          // Para PDFs, deixamos a extração de texto para a API
          extractedText = '[PDF Content - será processado pela IA]';
        } else {
          // Outros tipos de documento
          const arrayBuffer = result as ArrayBuffer;
          const bytes = new Uint8Array(arrayBuffer);
          let binary = '';
          for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
          }
          base64Data = btoa(binary);
          extractedText = '[Document Content - será processado pela IA]';
        }

        onProgress?.(95);

        resolve({
          ...file,
          base64: base64Data,
          metadata: {
            extractedText,
            pages: file.file.type === 'application/pdf' ? undefined : 1,
            ...file.metadata
          }
        });
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    // Ler como texto para arquivos de texto, como ArrayBuffer para outros
    if (file.file.type === 'text/plain' || file.file.type === 'text/markdown') {
      reader.readAsText(file.file);
    } else {
      reader.readAsArrayBuffer(file.file);
    }
  });
};

// Processar áudio
const processAudio = async (
  file: UploadedFile, 
  onProgress?: (progress: number) => void
): Promise<UploadedFile> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onprogress = (e) => {
      if (e.lengthComputable) {
        const progress = Math.round((e.loaded / e.total) * 60) + 10; // 10-70%
        onProgress?.(progress);
      }
    };

    reader.onload = (e) => {
      const result = e.target?.result as ArrayBuffer;
      
      if (!result) {
        reject(new Error('Failed to read audio file'));
        return;
      }

      onProgress?.(80);

      // Converter para base64
      const bytes = new Uint8Array(result);
      let binary = '';
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      const base64Data = btoa(binary);

      onProgress?.(90);

      // Tentar obter duração usando Audio element
      const audio = new Audio();
      const blob = new Blob([result], { type: file.file.type });
      const url = URL.createObjectURL(blob);
      
      audio.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        
        resolve({
          ...file,
          base64: base64Data,
          url,
          metadata: {
            duration: audio.duration,
            ...file.metadata
          }
        });
      };

      audio.onerror = () => {
        URL.revokeObjectURL(url);
        // Resolver sem duração se houver erro
        resolve({
          ...file,
          base64: base64Data,
          metadata: {
            ...file.metadata
          }
        });
      };

      audio.src = url;
    };

    reader.onerror = () => {
      reject(new Error('Failed to read audio file'));
    };

    reader.readAsArrayBuffer(file.file);
  });
};

// Processar vídeo
const processVideo = async (
  file: UploadedFile, 
  onProgress?: (progress: number) => void
): Promise<UploadedFile> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onprogress = (e) => {
      if (e.lengthComputable) {
        const progress = Math.round((e.loaded / e.total) * 60) + 10; // 10-70%
        onProgress?.(progress);
      }
    };

    reader.onload = (e) => {
      const result = e.target?.result as ArrayBuffer;
      
      if (!result) {
        reject(new Error('Failed to read video file'));
        return;
      }

      onProgress?.(80);

      // Converter para base64 (para vídeos pequenos apenas)
      if (file.size > 50 * 1024 * 1024) { // 50MB
        reject(new Error('Video file too large. Maximum 50MB supported.'));
        return;
      }

      const bytes = new Uint8Array(result);
      let binary = '';
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      const base64Data = btoa(binary);

      onProgress?.(90);

      // Tentar obter duração e dimensões usando Video element
      const video = document.createElement('video');
      const blob = new Blob([result], { type: file.file.type });
      const url = URL.createObjectURL(blob);
      
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        
        resolve({
          ...file,
          base64: base64Data,
          url,
          metadata: {
            duration: video.duration,
            width: video.videoWidth,
            height: video.videoHeight,
            ...file.metadata
          }
        });
      };

      video.onerror = () => {
        URL.revokeObjectURL(url);
        // Resolver sem metadata se houver erro
        resolve({
          ...file,
          base64: base64Data,
          metadata: {
            ...file.metadata
          }
        });
      };

      video.src = url;
    };

    reader.onerror = () => {
      reject(new Error('Failed to read video file'));
    };

    reader.readAsArrayBuffer(file.file);
  });
};

// Utilitário para redimensionar imagem
export const resizeImage = (
  canvas: HTMLCanvasElement,
  targetWidth: number,
  targetHeight: number,
  quality: number = 0.8
): string => {
  return canvas.toDataURL('image/jpeg', quality);
};

// Utilitário para extrair texto de imagem (OCR básico)
export const extractTextFromImage = async (imageBase64: string): Promise<string> => {
  // Esta função seria implementada com uma biblioteca de OCR como Tesseract.js
  // Por enquanto, retornamos uma mensagem indicativa
  return '[OCR não implementado - texto será extraído pela IA]';
};

// Utilitário para obter thumbnail de vídeo
export const getVideoThumbnail = (video: HTMLVideoElement, time: number = 1): string => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  if (!ctx) return '';
  
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  
  video.currentTime = time;
  ctx.drawImage(video, 0, 0);
  
  return canvas.toDataURL('image/jpeg', 0.8);
};